export interface FileUploadResult {
  callId: string;
  fileName: string;
  blobUrl: string;
  fileSize: number;
  contentType: string;
  success: boolean;
  errorMessage?: string;
}

export interface FileUploadResponse {
  results: FileUploadResult[];
  totalFiles: number;
  successfulUploads: number;
  failedUploads: number;
  errors: string[];
}

export interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  errorMessage?: string;
}

export interface FileWithMetadata {
  file: File;
  id: string;
  progress: number;
  status: 'pending' | 'uploading' | 'verifying' | 'completed' | 'error';
  errorMessage?: string;
}

export interface QuotaStatus {
  used: number;
  limit: number;
  remaining: number;
  canUpload: boolean;
  maxAudioDurationMinutes: number;
  resetsAt: string;
}

export interface UploadPresignResult {
  success: boolean;
  callId: string;
  sasUrl: string;
  expiresAt: string;
  errorMessage?: string;
  quotaExceeded: boolean;
  /** Backend kill-switch (Upload.DirectUploadEnabled=false): use the relay for the whole session. */
  directUploadDisabled: boolean;
}

export interface UploadMetadata {
  incidentNumber?: string;
  callType: string;
  callDate?: string;
}

export type UploadItemStatus =
  | 'pending'
  | 'presigning'
  | 'uploading'
  | 'confirming'
  // Transport error reported, but the server may have committed the row before the response was
  // lost to a gateway timeout — the session-end reconcile is probing the idempotency key.
  | 'verifying'
  | 'completed'
  | 'error';

/** A file owned by the app-level UploadContext — survives route changes. */
export interface UploadQueueItem {
  id: string;
  file: File;
  status: UploadItemStatus;
  progress: number;
  errorMessage?: string;
  /** Idempotency key sent (as X-Upload-Dedupe-Id) on every relay attempt for this file, so a
   *  retried POST whose response was lost resolves to the existing call instead of duplicating it. */
  dedupeId: string;
}


export const ALLOWED_AUDIO_TYPES = [
  'audio/mpeg',
  'audio/mp3', 
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
  'audio/mp4',
  'audio/m4a',
  'audio/aac',
  'audio/flac',
  'audio/ogg',
  'audio/x-ms-wma',
  'application/octet-stream'
];

export const ALLOWED_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.aac', '.flac', '.ogg', '.wma'];

export const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB per file — keep in sync with backend MaxFileSizeBytes
export const MAX_FILES_PER_UPLOAD = 50;